/* AgentMeeting — Admin dashboard
 *
 * Tabs: Stats / Users / Transactions / Meetings.
 * All requests go through the typed `admin` helpers in shared/api.ts. The
 * server still gates /api/admin/* at the user level (is_admin); on a 403 we
 * render a friendly "管理员专用" card instead of a raw error.
 */

import React from 'react';
import { MarketingBar } from '../shared/marketing-bar';
import { admin as adminApi, HTTPError } from '../shared/api';

type Tab = 'stats' | 'users' | 'transactions' | 'meetings';

const fmtCents = (c: number) => `$${(c / 100).toFixed(2)}`;

function fmtDate(iso?: string) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toISOString().slice(0, 19).replace('T', ' ');
  } catch { return iso; }
}

function shortId(id?: string) {
  if (!id) return '—';
  return id.length > 8 ? id.slice(0, 8) : id;
}

function truncate(s: string | undefined, n: number) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

export function Admin({ goTo }: { goTo: (r: string) => void }) {
  const [tab, setTab] = React.useState<Tab>('stats');
  const [forbidden, setForbidden] = React.useState(false);
  const [pageErr, setPageErr] = React.useState<string>('');

  const tabs: { id: Tab; label: string }[] = [
    { id: 'stats',        label: 'Stats' },
    { id: 'users',        label: 'Users' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'meetings',     label: 'Meetings' },
  ];

  const handleErr = (err: unknown) => {
    if (err instanceof HTTPError && err.status === 403) {
      setForbidden(true);
      setPageErr('');
      return;
    }
    if (err instanceof HTTPError) {
      setPageErr(`HTTP ${err.status} · ${typeof err.body === 'string' ? err.body : (err.body?.error || err.message)}`);
      return;
    }
    setPageErr(String((err as any)?.message || err));
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-0)', color: 'var(--fg-0)' }}>
      <MarketingBar here="admin" goTo={goTo} signedIn={true} />

      <div className="mkt-container" style={{ padding: '32px 28px' }}>
        <div className="flex between" style={{ alignItems: 'flex-end', marginBottom: 20 }}>
          <div>
            <span className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.16em' }}>
              [ADMIN]
            </span>
            <h1 style={{ fontSize: 32, fontWeight: 500, letterSpacing: '-0.02em', margin: '8px 0 4px' }}>
              管理后台
            </h1>
            <p className="meta" style={{ margin: 0 }}>
              用户 · 交易 · 会议 · 全局指标
            </p>
          </div>
          <div className="flex gap-2">
            <button className="btn ghost mono" onClick={() => goTo('app/meetings')}>← back to app</button>
          </div>
        </div>

        {forbidden ? (
          <ForbiddenCard goTo={goTo} />
        ) : (
          <>
            {pageErr && (
              <div className="mono tiny" style={{
                border: '1px solid var(--err)', borderRadius: 6,
                padding: '10px 14px', marginBottom: 16, background: 'var(--bg-1)',
                color: 'var(--err)',
              }}>
                {pageErr}
              </div>
            )}

            {/* tab bar — sliver style */}
            <div className="sliver" style={{
              border: '1px solid var(--line-1)',
              borderRadius: 6, marginBottom: 20, height: 36,
            }}>
              {tabs.map(t => (
                <button
                  key={t.id}
                  className={tab === t.id ? 'active' : ''}
                  onClick={() => { setTab(t.id); setPageErr(''); }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'stats'        && <StatsTab onErr={handleErr} />}
            {tab === 'users'        && <UsersTab onErr={handleErr} />}
            {tab === 'transactions' && <TransactionsTab onErr={handleErr} goTo={goTo} />}
            {tab === 'meetings'     && <MeetingsTab onErr={handleErr} />}
          </>
        )}
      </div>
    </div>
  );
}

function ForbiddenCard({ goTo }: { goTo: (r: string) => void }) {
  return (
    <div style={{
      border: '1px solid var(--judge)', borderRadius: 6,
      background: 'var(--bg-1)', padding: 32,
      textAlign: 'center', maxWidth: 560, margin: '40px auto',
    }}>
      <div className="mono tiny" style={{
        color: 'var(--judge)', letterSpacing: '0.12em', marginBottom: 12,
      }}>
        [403 · ADMIN ONLY]
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 500, margin: '8px 0' }}>管理员专用</h2>
      <p className="meta" style={{ fontSize: 13, lineHeight: 1.6, marginTop: 8 }}>
        此页面仅对具有 admin 标记的账户可见。
        如果你认为这是个误会, 请联系站点管理员授权你的账号。
      </p>
      <div className="flex center" style={{ justifyContent: 'center', gap: 8, marginTop: 18 }}>
        <button className="btn primary mono" onClick={() => goTo('landing')}>← back to home</button>
        <button className="btn ghost mono" onClick={() => goTo('app/meetings')}>open app</button>
      </div>
    </div>
  );
}

/* ─── Stats tab ───────────────────────────────────────────────────────── */

function StatsTab({ onErr }: { onErr: (e: unknown) => void }) {
  const [data, setData] = React.useState<Awaited<ReturnType<typeof adminApi.stats>> | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi.stats()
      .then(s => { if (!cancelled) { setData(s); setLoading(false); } })
      .catch(e => { if (!cancelled) { onErr(e); setLoading(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loading />;
  if (!data) return <Empty label="no stats available" />;

  const cards = [
    { tag: 'TOTAL USERS',        value: String(data.total_users),                 accent: 'var(--live)' },
    { tag: 'TOTAL MEETINGS',     value: String(data.total_meetings),              accent: 'var(--judge)' },
    { tag: 'TOTAL MESSAGES',     value: String(data.total_messages),              accent: 'var(--fg-0)' },
    { tag: 'TOTAL SPENT',        value: fmtCents(data.total_spent_cents),         accent: 'var(--err)' },
    { tag: 'CREDITS OUTSTANDING', value: fmtCents(data.total_credits_outstanding_cents), accent: 'var(--neutral)' },
  ];

  return (
    <div className="grid" style={{
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12,
    }}>
      {cards.map(c => (
        <div key={c.tag} style={{
          padding: 18, border: '1px solid var(--line-2)',
          borderRadius: 6, background: 'var(--bg-1)',
        }}>
          <div className="mono tiny" style={{ color: c.accent, letterSpacing: '0.1em' }}>
            [{c.tag}]
          </div>
          <div style={{
            fontSize: 32, fontWeight: 500, letterSpacing: '-0.02em',
            marginTop: 10,
          }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

/* ─── Users tab ───────────────────────────────────────────────────────── */

function UsersTab({ onErr }: { onErr: (e: unknown) => void }) {
  const [users, setUsers] = React.useState<any[] | null>(null);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const [rechargeFor, setRechargeFor] = React.useState<string | null>(null);
  const limit = 50;
  const [offset, setOffset] = React.useState(0);

  const reload = () => setReloadKey(k => k + 1);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi.listUsers({ limit, offset })
      .then(r => { if (!cancelled) { setUsers(r.users || []); setTotal(r.total); setLoading(false); } })
      .catch(e => { if (!cancelled) { onErr(e); setLoading(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, offset]);

  if (loading) return <Loading />;
  if (!users || users.length === 0) return <Empty label="no users yet" />;

  return (
    <div style={{
      border: '1px solid var(--line-2)', borderRadius: 6,
      background: 'var(--bg-1)', overflow: 'hidden',
    }}>
      <div className="grid" style={{
        gridTemplateColumns:
          '90px 1.4fr 1fr 70px 90px 70px 90px 60px 110px 200px',
        padding: '10px 14px', background: 'var(--bg-2)',
        borderBottom: '1px solid var(--line-1)',
        fontFamily: 'var(--mono)', fontSize: 10,
        color: 'var(--fg-3)', letterSpacing: '0.08em',
      }}>
        <div>ID</div>
        <div>EMAIL</div>
        <div>NAME</div>
        <div>PLAN</div>
        <div>CREDITS</div>
        <div>MTGS</div>
        <div>SPENT</div>
        <div>ADMIN</div>
        <div>JOINED</div>
        <div style={{ textAlign: 'right' }}>ACTIONS</div>
      </div>

      {users.map((u, i) => {
        const isOpen = expanded === u.id;
        const isRech = rechargeFor === u.id;
        return (
          <div key={u.id} style={{
            borderBottom: i < users.length - 1 ? '1px solid var(--line-1)' : 'none',
          }}>
            <div className="grid" style={{
              gridTemplateColumns:
                '90px 1.4fr 1fr 70px 90px 70px 90px 60px 110px 200px',
              padding: '10px 14px', fontSize: 12, alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => setExpanded(isOpen ? null : u.id)}
            >
              <div className="mono dim" title={u.id}>{shortId(u.id)}</div>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
              <div className="dim" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || '—'}</div>
              <div className="mono tiny">{u.plan || 'hobby'}</div>
              <div className="mono" style={{ color: 'var(--live)' }}>{fmtCents(u.credits_cents || 0)}</div>
              <div className="mono">{u.meetings_count ?? 0}</div>
              <div className="mono dim">{fmtCents(u.total_spent_cents || 0)}</div>
              <div>
                {u.is_admin
                  ? <span className="tag mono tiny" style={{ borderColor: 'var(--judge)', color: 'var(--judge)' }}>admin</span>
                  : <span className="tag mono tiny dim">user</span>}
              </div>
              <div className="mono tiny dim">{fmtDate(u.created_at).slice(0, 10)}</div>
              <div className="flex" style={{ justifyContent: 'flex-end', gap: 6 }} onClick={e => e.stopPropagation()}>
                <button
                  className="btn tiny mono"
                  onClick={() => setRechargeFor(isRech ? null : u.id)}
                >
                  $ recharge
                </button>
                <button
                  className="btn tiny mono ghost"
                  onClick={async () => {
                    try {
                      await adminApi.patchUser(u.id, { is_admin: !u.is_admin });
                      reload();
                    } catch (err) {
                      onErr(err);
                    }
                  }}
                >
                  ⇄ admin
                </button>
              </div>
            </div>

            {isRech && (
              <RechargeForm
                userId={u.id}
                onCancel={() => setRechargeFor(null)}
                onDone={() => { setRechargeFor(null); reload(); }}
                onErr={onErr}
              />
            )}

            {isOpen && (
              <UserTransactions userId={u.id} onErr={onErr} />
            )}
          </div>
        );
      })}

      <div className="flex between center" style={{
        padding: '10px 14px', borderTop: '1px solid var(--line-1)',
        background: 'var(--bg-2)',
      }}>
        <span className="mono tiny dim">
          showing {offset + 1}–{Math.min(offset + limit, total)} of {total}
        </span>
        <div className="flex gap-2">
          <button
            className="btn tiny mono ghost"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
          >← prev</button>
          <button
            className="btn tiny mono ghost"
            disabled={offset + limit >= total}
            onClick={() => setOffset(offset + limit)}
          >next →</button>
        </div>
      </div>
    </div>
  );
}

function RechargeForm({
  userId,
  onCancel,
  onDone,
  onErr,
}: {
  userId: string;
  onCancel: () => void;
  onDone: () => void;
  onErr: (e: unknown) => void;
}) {
  const [amount, setAmount] = React.useState('');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [localErr, setLocalErr] = React.useState('');

  const submit = async () => {
    setLocalErr('');
    const cents = parseInt(amount.trim(), 10);
    if (!Number.isFinite(cents) || cents === 0) {
      setLocalErr('amount must be a non-zero integer in cents');
      return;
    }
    setBusy(true);
    try {
      await adminApi.recharge(userId, { amount_cents: cents, note: note.trim() || undefined });
      onDone();
    } catch (err) {
      if (err instanceof HTTPError) {
        setLocalErr(`HTTP ${err.status} · ${typeof err.body === 'string' ? err.body : (err.body?.error || err.message)}`);
      } else {
        setLocalErr(String((err as any)?.message || err));
      }
      onErr(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      padding: '12px 14px', background: 'var(--bg-2)',
      borderTop: '1px dashed var(--line-1)',
      borderBottom: '1px dashed var(--line-1)',
    }}>
      <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.1em', marginBottom: 8 }}>
        [RECHARGE · cents · positive=credit, negative=debit]
      </div>
      <div className="flex gap-2" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="col" style={{ minWidth: 160 }}>
          <label className="mono tiny dim" style={{ marginBottom: 4 }}>amount (cents)</label>
          <input
            className="input mono"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="e.g. 1000  (= $10.00)"
            inputMode="numeric"
          />
        </div>
        <div className="col grow" style={{ minWidth: 240 }}>
          <label className="mono tiny dim" style={{ marginBottom: 4 }}>note (optional)</label>
          <textarea
            className="input mono"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="why this adjustment"
            rows={2}
          />
        </div>
        <div className="flex gap-2" style={{ marginTop: 18 }}>
          <button className="btn primary mono tiny" onClick={submit} disabled={busy}>
            {busy ? '…' : '$ submit'}
          </button>
          <button className="btn ghost mono tiny" onClick={onCancel} disabled={busy}>
            cancel
          </button>
        </div>
      </div>
      {localErr && (
        <div className="mono tiny" style={{ color: 'var(--err)', marginTop: 8 }}>{localErr}</div>
      )}
    </div>
  );
}

function UserTransactions({ userId, onErr }: { userId: string; onErr: (e: unknown) => void }) {
  const [data, setData] = React.useState<{ transactions: any[] } | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi.getUser(userId)
      .then(r => { if (!cancelled) { setData({ transactions: r.transactions || [] }); setLoading(false); } })
      .catch(e => { if (!cancelled) { onErr(e); setLoading(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return (
    <div style={{
      padding: '10px 14px', background: 'var(--bg-2)',
      borderTop: '1px dashed var(--line-1)',
    }}>
      <div className="mono tiny" style={{ color: 'var(--judge)', letterSpacing: '0.1em', marginBottom: 8 }}>
        [RECENT TRANSACTIONS · last 50]
      </div>
      {loading && <div className="mono tiny dim">loading…</div>}
      {!loading && data && data.transactions.length === 0 && (
        <div className="mono tiny dim">no transactions</div>
      )}
      {!loading && data && data.transactions.length > 0 && (
        <div className="col gap-1">
          {data.transactions.map(tx => (
            <div key={tx.id} className="flex gap-3 mono tiny" style={{
              padding: '4px 0', borderBottom: '1px solid var(--line-1)',
              alignItems: 'center',
            }}>
              <span style={{ minWidth: 110, color: 'var(--fg-3)' }}>
                {fmtDate(tx.created_at)}
              </span>
              <KindBadge kind={tx.kind} />
              <span className="mono" style={{
                minWidth: 80, textAlign: 'right',
                color: (tx.amount_cents || 0) < 0 ? 'var(--err)' : 'var(--live)',
              }}>
                {(tx.amount_cents || 0) >= 0 ? '+' : ''}{fmtCents(tx.amount_cents || 0)}
              </span>
              <span className="dim" style={{ minWidth: 90, textAlign: 'right' }}>
                bal {fmtCents(tx.balance_after_cents || 0)}
              </span>
              <span className="dim" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {tx.note || tx.meeting_id || ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Transactions tab ─────────────────────────────────────────────────── */

function TransactionsTab({
  onErr,
  goTo,
}: {
  onErr: (e: unknown) => void;
  goTo: (r: string) => void;
}) {
  const [kind, setKind] = React.useState<string>('');
  const [txs, setTxs] = React.useState<any[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const limit = 100;
  const [offset, setOffset] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi.listTransactions({
      limit, offset,
      kind: kind || undefined,
    })
      .then(r => { if (!cancelled) { setTxs(r.transactions || []); setLoading(false); } })
      .catch(e => { if (!cancelled) { onErr(e); setLoading(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, offset]);

  return (
    <div className="col gap-3">
      <div className="flex gap-2 center" style={{ alignItems: 'center' }}>
        <label className="mono tiny dim">filter:</label>
        <select
          className="input mono"
          value={kind}
          onChange={e => { setKind(e.target.value); setOffset(0); }}
          style={{ width: 220 }}
        >
          <option value="">all kinds</option>
          <option value="signup_gift">signup_gift</option>
          <option value="recharge">recharge</option>
          <option value="consumption">consumption</option>
          <option value="refund">refund</option>
          <option value="admin_adjust">admin_adjust</option>
        </select>
        <span className="mono tiny dim" style={{ marginLeft: 'auto' }}>
          {txs ? `${txs.length} rows` : ''}
        </span>
      </div>

      {loading ? (
        <Loading />
      ) : !txs || txs.length === 0 ? (
        <Empty label="no transactions" />
      ) : (
        <div style={{
          border: '1px solid var(--line-2)', borderRadius: 6,
          background: 'var(--bg-1)', overflow: 'hidden',
        }}>
          <div className="grid" style={{
            gridTemplateColumns: '140px 110px 100px 110px 1fr 110px 1fr',
            padding: '10px 14px', background: 'var(--bg-2)',
            borderBottom: '1px solid var(--line-1)',
            fontFamily: 'var(--mono)', fontSize: 10,
            color: 'var(--fg-3)', letterSpacing: '0.08em',
          }}>
            <div>WHEN</div>
            <div>KIND</div>
            <div>AMOUNT</div>
            <div>BALANCE</div>
            <div>USER</div>
            <div>MEETING</div>
            <div>NOTE</div>
          </div>
          {txs.map((tx, i) => (
            <div key={tx.id} className="grid" style={{
              gridTemplateColumns: '140px 110px 100px 110px 1fr 110px 1fr',
              padding: '10px 14px', fontSize: 12,
              alignItems: 'center',
              borderBottom: i < txs.length - 1 ? '1px solid var(--line-1)' : 'none',
            }}>
              <div className="mono tiny dim">{fmtDate(tx.created_at)}</div>
              <div><KindBadge kind={tx.kind} /></div>
              <div className="mono" style={{
                color: (tx.amount_cents || 0) < 0 ? 'var(--err)' : 'var(--live)',
              }}>
                {(tx.amount_cents || 0) >= 0 ? '+' : ''}{fmtCents(tx.amount_cents || 0)}
              </div>
              <div className="mono dim">{fmtCents(tx.balance_after_cents || 0)}</div>
              <div className="dim" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {tx.user_email || shortId(tx.user_id)}
              </div>
              <div>
                {tx.meeting_id ? (
                  <a
                    className="link mono tiny"
                    onClick={() => {
                      try { localStorage.setItem('agentmeeting.currentMeetingId', tx.meeting_id); } catch { /* noop */ }
                      goTo('app/replay');
                    }}
                  >{shortId(tx.meeting_id)}</a>
                ) : (
                  <span className="mono tiny dim">—</span>
                )}
              </div>
              <div className="dim" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {tx.note || ''}
              </div>
            </div>
          ))}

          <div className="flex between center" style={{
            padding: '10px 14px', borderTop: '1px solid var(--line-1)',
            background: 'var(--bg-2)',
          }}>
            <span className="mono tiny dim">offset {offset} · limit {limit}</span>
            <div className="flex gap-2">
              <button
                className="btn tiny mono ghost"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
              >← prev</button>
              <button
                className="btn tiny mono ghost"
                disabled={txs.length < limit}
                onClick={() => setOffset(offset + limit)}
              >next →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Meetings tab ───────────────────────────────────────────────────── */

function MeetingsTab({ onErr }: { onErr: (e: unknown) => void }) {
  const [list, setList] = React.useState<any[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const limit = 100;
  const [offset, setOffset] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi.listMeetings({ limit, offset })
      .then(r => { if (!cancelled) { setList(r.meetings || []); setLoading(false); } })
      .catch(e => { if (!cancelled) { onErr(e); setLoading(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  if (loading) return <Loading />;
  if (!list || list.length === 0) return <Empty label="no meetings" />;

  return (
    <div style={{
      border: '1px solid var(--line-2)', borderRadius: 6,
      background: 'var(--bg-1)', overflow: 'hidden',
    }}>
      <div className="grid" style={{
        gridTemplateColumns: '1.2fr 1.4fr 1fr 90px 110px 110px 130px',
        padding: '10px 14px', background: 'var(--bg-2)',
        borderBottom: '1px solid var(--line-1)',
        fontFamily: 'var(--mono)', fontSize: 10,
        color: 'var(--fg-3)', letterSpacing: '0.08em',
      }}>
        <div>TITLE</div>
        <div>TOPIC</div>
        <div>OWNER</div>
        <div>STATUS</div>
        <div>BUDGET / SPENT</div>
        <div>TOKENS IN/OUT</div>
        <div>CREATED</div>
      </div>
      {list.map((m, i) => (
        <div key={m.id} className="grid" style={{
          gridTemplateColumns: '1.2fr 1.4fr 1fr 90px 110px 110px 130px',
          padding: '10px 14px', fontSize: 12,
          alignItems: 'center',
          borderBottom: i < list.length - 1 ? '1px solid var(--line-1)' : 'none',
        }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {m.title || <span className="dim">(untitled)</span>}
          </div>
          <div className="dim" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.topic}>
            {truncate(m.topic, 60)}
          </div>
          <div className="mono tiny dim" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {m.owner_email || shortId(m.owner_id)}
          </div>
          <div>
            <StatusBadge status={m.status} />
          </div>
          <div className="mono tiny">
            <span className="dim">{fmtCents(m.budget_cents || 0)}</span>
            {' / '}
            <span style={{ color: 'var(--live)' }}>{fmtCents(m.spent_cents || 0)}</span>
          </div>
          <div className="mono tiny dim">
            {(m.tokens_in ?? 0).toLocaleString()} / {(m.tokens_out ?? 0).toLocaleString()}
          </div>
          <div className="mono tiny dim">{fmtDate(m.created_at)}</div>
        </div>
      ))}

      <div className="flex between center" style={{
        padding: '10px 14px', borderTop: '1px solid var(--line-1)',
        background: 'var(--bg-2)',
      }}>
        <span className="mono tiny dim">offset {offset} · limit {limit}</span>
        <div className="flex gap-2">
          <button
            className="btn tiny mono ghost"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
          >← prev</button>
          <button
            className="btn tiny mono ghost"
            disabled={list.length < limit}
            onClick={() => setOffset(offset + limit)}
          >next →</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Shared bits ─────────────────────────────────────────────────────── */

function KindBadge({ kind }: { kind?: string }) {
  let color = 'var(--fg-3)';
  if (kind === 'consumption')      color = 'var(--err)';
  else if (kind === 'recharge')    color = 'var(--live)';
  else if (kind === 'signup_gift') color = 'var(--judge)';
  else if (kind === 'refund')      color = 'var(--neutral)';
  else if (kind === 'admin_adjust') color = 'var(--pro)';
  return (
    <span className="tag mono tiny" style={{
      borderColor: color, color, whiteSpace: 'nowrap',
    }}>
      {kind || '—'}
    </span>
  );
}

function StatusBadge({ status }: { status?: string }) {
  let color = 'var(--fg-3)';
  if (status === 'live' || status === 'running') color = 'var(--live)';
  else if (status === 'ended' || status === 'completed') color = 'var(--judge)';
  else if (status === 'paused') color = 'var(--neutral)';
  else if (status === 'error') color = 'var(--err)';
  return (
    <span className="tag mono tiny" style={{
      borderColor: color, color,
    }}>
      {status || '—'}
    </span>
  );
}

function Loading() {
  return (
    <div style={{
      padding: 32, textAlign: 'center',
      border: '1px dashed var(--line-2)', borderRadius: 6,
      background: 'var(--bg-1)',
    }}>
      <span className="mono tiny dim">loading…</span>
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return (
    <div style={{
      padding: 32, textAlign: 'center',
      border: '1px dashed var(--line-2)', borderRadius: 6,
      background: 'var(--bg-1)',
    }}>
      <span className="mono tiny dim">{label}</span>
    </div>
  );
}
